/**
 * 屏 5/5.5 — 战斗结果页（Ardot v3 定稿版 · 2026-07-07）
 *
 * 设计目标（按 GDD FR-screen5-result-redesign.md v0.2 + 第 10 章最终决策）：
 * - 单屏内完成 4 层叙事：Hero / 证据回放 / 浮窗战术板 / 行动收口
 * - 不再分页，1440×920 一次性看全
 * - 65/35 不对称：左栏证据回放 910px（地图+指标+log），右栏浮窗战术板 430px
 * - 浮窗战术板：4 角红色 L 括号 + FLOATING PANEL 角标 + 4 段（雷达→战况分析→AAR→Chat）
 * - 不调 LLM：AAR 5 点用 buildBattleAnalysis + tacticalDimensions 规则推导
 * - 雷达 hover tooltip：SVG <title> 元素（零 JS）
 * - A 字段固定 0（demo 1v1 无助攻）
 * - Log 卡固定 5 行，无折叠入口
 * - CTA 仅 3 按钮：REMATCH / MODIFY CODE / BACK TO HUB
 */

import { getProject } from "../storage/project-store";
import { pathFor } from "../router/screen-router";
import { generateBattleAnalysisStream } from "../services/llm-service";
import { t } from "../i18n";
import { listCommits, type Commit } from "../services/git-service";
// V5: 教学进度记录
import { recordAttempt, markLevelCompleted, getNextUnlockedLevel, loadProgress } from "../services/progress-store";
// V5 P1: 晋级动画
import { showRankUpAnimation, getRankUpData } from "../ui/rank-up-animation";
// V9: 作品保存
import { addForgeProject } from "../storage/works-store";
import { calculateForgeScore } from "../utils/forge-scoring";
import { getForgeLevelDifficulty, getMaxTimeForLevel } from "../utils/forge-difficulty";
import { showAchievementNotification } from "../ui/achievement-notification";
import { markForgeLevelCompleted } from "../services/forge-progress-service";
// V12: 数据中心 — 战斗历史记录
import { createBattleRecord } from "../core/battle-history";
import { pushOneAndWait } from "../core/sync-client";
import { isOnboarding, setOnboardingStep, completeOnboarding } from "../onboarding/onboarding-state";

/** 压缩录像：屏 4 的 delta 编码方案（仅存储变化字段 + 跳过 idle tick） */
interface CompressedReplay {
  header: { map_id: string; map_size: { width: number; height: number }; self_max_hp: number; enemy_max_hp: number; attack_cooldown_ticks: number };
  ticks: CompressedReplayTick[];
}

interface ReplayDelta {
  selfPos?: MapPos;
  enemyPos?: MapPos;
  selfHp?: number;
  enemyHp?: number;
  selfCooldown?: number;
  selfAlive?: boolean;
  enemyAlive?: boolean;
  enemyVisible?: boolean;
  enemyLkp?: MapPos | null;
  nearestCover?: MapPos | null;
  warning?: string | null;
}

interface CompressedReplayTick {
  tick: number;
  delta: ReplayDelta;
  playerAction: unknown;
  aiAction: unknown;
}

interface ConsoleEntry {
  tick: number;
  tag: string;
  team: string;
  message: string;
  fileLine?: string;
}

interface MapPos {
  x: number;
  y: number;
}

interface BattleSummary {
  winner: "team_a" | "team_b" | "draw";
  reason: string;
  totalTicks: number;
  playerKills: number;
  playerDeaths: number;
  durationMs: number;
  replay?: CompressedReplay;
  keyEvents?: Array<{ eventType: string; tickNum: number; actor: "team_a" | "team_b"; description: string }>;
  playerShotsFired?: number;
  playerDeathTick?: number | null;
  playerFinalHp?: number;
  enemyFinalHp?: number;
  consoleLog?: ConsoleEntry[];
  playerActions?: { move: number; shoot: number; hit: number; damage: number };
  aiActions?: { move: number; shoot: number; hit: number; damage: number };
  /** demo override：25×25 战场坐标（缺失则用占位） */
  playerPos?: MapPos;
  enemyPos?: MapPos;
  coverBlocks?: MapPos[];
  gridSize?: number;
  /** v34：屏 5 地图真实数据（屏 4 battle_end 注入），缺失则 fallback 占位 */
  mapId?: string;
  obstacleBlocks?: MapPos[];
  spawnA?: MapPos;
  spawnB?: MapPos;
}

type ResultTheme = "win" | "draw" | "lose";

type TacticalDimension = {
  key: string;
  label: string;
  score: number;
  verdict: string;
  evidence: string;
  icon: string;
};

type BattleLogView = {
  tick: number;
  tag: string;
  team: string;
  message: string;
  fileLine?: string;
  filter?: string;
};

type KeyEventView = {
  id: string;
  tick: number;
  side: string;
  label: string;
  description: string;
};

type BattleAnalysis = {
  overview: string;
  keyPointText: string;
  nextStep: string;
  logsPreview: BattleLogView[];
};

type AARPoint = {
  index: number;     // 01..05
  tone: "lose" | "warn" | "good";
  text: string;
};

/** 6 维雷达顺序（顺时针从顶部开始），对应 tacticalDimensions 数组下标 */
const RADAR_LABEL_SHORT = [t("result.radar_pos"), t("result.radar_prs"), t("result.radar_vis"), t("result.radar_sht"), t("result.radar_sur"), t("result.radar_tmp")];

export async function renderScreen5Result(root: HTMLElement, projectId: string, onNavigate: (path: string) => void) {
  const project = getProject(projectId);
  if (!project) {
    root.innerHTML = `<div class="error">项目不存在：${projectId}</div>`;
    return;
  }

  const raw = sessionStorage.getItem(`battle-${projectId}`);
  // F1: 读后即删 — 一次性消费模式,避免跨屏泄露残留
  if (raw) sessionStorage.removeItem(`battle-${projectId}`);
  const summary: BattleSummary = raw
    ? JSON.parse(raw)
    : {
        winner: "draw",
        reason: t("result.no_data"),
        totalTicks: 0,
        playerKills: 0,
        playerDeaths: 0,
        durationMs: 0,
      };

  // V12 数据中心：保存战斗记录（有实际战斗数据时）
  if (raw) {
    try {
      const resultMap: Record<string, "win" | "loss" | "draw"> = {
        team_a: "win",
        team_b: "loss",
        draw: "draw",
      };
      createBattleRecord({
        projectId,
        playerTeam: "A",
        result: resultMap[summary.winner] ?? "draw",
        opponentName: "AI Easy Aggressive",
        opponentCode: "",
        battleDuration: summary.totalTicks,
        levelId: project.mapId || "battle_easy",
        mapName: project.name || "battle_easy",
        remainingHP: summary.playerFinalHp ?? 0,
        enemyRemainingHP: summary.enemyFinalHp ?? 0,
      });
      // 战斗提交完成后等待 battle-history 写入服务端。
      await pushOneAndWait("battle-history");
    } catch (err) {
      console.warn("[screen-5] 保存战斗记录失败:", err);
    }
  }

  // P1-2: 来源上下文感知（铸剑阁 → 结果页的返回/下一关应回到铸剑阁）
  const resultSource = localStorage.getItem("battlecode-result-source") ?? "";
  if (resultSource) localStorage.removeItem("battlecode-result-source");
  const fromForge = resultSource.startsWith("forge:");

  // V5: 教学关卡进度记录
  const levelId = project.levelId;
  const isV5Level = !!(levelId && levelId.startsWith("v5_"));
  let v5PhaseUnlocked = false;
  let v5NextLevel: string | null = null;
  if (isV5Level && levelId && raw) {
    const prevProgress = loadProgress();
    const prevPhase = prevProgress.currentPhase;
    recordAttempt(levelId);
    if (summary.winner === "team_a") {
      const result = markLevelCompleted(levelId, { ticks: summary.totalTicks });
      v5PhaseUnlocked = result.phaseUnlocked;
      // V5 P1: 阶段晋级动画
      if (v5PhaseUnlocked) {
        const newPhase = (prevProgress.currentPhase ?? 0) + 1;
        const rankUpData = getRankUpData(prevPhase, newPhase);
        // 延迟显示晋级动画（等页面渲染完成后）
        if (rankUpData) {
          setTimeout(() => {
            showRankUpAnimation(root, rankUpData);
          }, 1000);
        }
      }
    }
    v5NextLevel = getNextUnlockedLevel();
  }

  // V9: 铸剑阁作品保存（战斗完成后自动保存到我的作品集）
  if (fromForge && summary.winner === "team_a" && raw) {
    try {
      // resultSource 格式: "forge:ch${activeChapter}:${id}" 或 "forge:ch${ch.num}:exam"
      const forgeMatch = resultSource.match(/^forge:ch(\d+):(.+)$/);
      if (forgeMatch) {
        const chapterNum = parseInt(forgeMatch[1], 10);
        const forgeLevelId = forgeMatch[2];
        
        // 计算评分和难度
        const maxTime = getMaxTimeForLevel(chapterNum, forgeLevelId);
        const score = calculateForgeScore(
          summary.playerFinalHp ?? 0,
          summary.totalTicks,
          maxTime
        );
        const difficulty = getForgeLevelDifficulty(chapterNum, forgeLevelId);
        
        // 保存作品
        addForgeProject({
          chapterNum,
          levelId: forgeLevelId,
          levelName: project.name || `Chapter ${chapterNum}`,
          state: "completed",
          score,
          timeUsed: summary.totalTicks,
          difficulty,
          completedAt: Date.now(),
        });
        
        // 记录铸剑阁进度（逐级解锁）
        markForgeLevelCompleted(chapterNum, forgeLevelId);
        
        // 显示成就通知
        showAchievementNotification(root, {
          title: "新成就解锁",
          levelName: project.name || `Chapter ${chapterNum}`,
          score,
          difficulty,
          timeUsed: summary.totalTicks,
        });
      }
    } catch (err) {
      console.warn("[screen-5] 保存铸剑阁作品失败:", err);
    }
  }

  const opId = sessionStorage.getItem(`op-${projectId}`) ?? "OP-01";
  const theme: ResultTheme = summary.winner === "team_a" ? "win" : summary.winner === "draw" ? "draw" : "lose";
  const durationClock = formatDuration(summary.durationMs);
  const playerHp = summary.playerFinalHp ?? 0;
  const enemyHp = summary.enemyFinalHp ?? 0;
  const shotsFired = summary.playerShotsFired ?? 0;
  const totalTicks = summary.totalTicks;
  const ticksPerSecond = summary.durationMs > 0 ? totalTicks / (summary.durationMs / 1000) : 0;
  const gridSize = summary.gridSize ?? 25;

  const tacticalDimensions = buildTacticalDimensions(summary);
  const keyEvents = normalizeKeyEvents(summary);
  const logs = normalizeConsoleLogs(summary);
  buildBattleAnalysis(summary, tacticalDimensions, keyEvents, logs);
  const aarPoints = buildAARReviewList(tacticalDimensions, keyEvents, logs, summary);

  // ===== 渲染 =====
  const heroWord = theme === "win" ? t("result.victory") : theme === "draw" ? t("result.draw") : t("result.defeat");

  root.innerHTML = `
    <div class="screen screen-5 result-screen result-state-${theme}">
      <header class="screen-header result-hud result-hud-compact">
        <div class="result-hud-left">
          ${opId ? `<span class="result-hud-chip">${escapeHtml(opId)}</span>` : ""}
          <span class="result-hud-sep">·</span>
          <span class="result-hud-tag">${t("result.battle_result")}</span>
          <span class="result-hud-sep">//</span>
          <span class="result-hud-status">${t("result.after_action")}</span>
        </div>
        <div class="result-hud-right">
          <span class="result-hud-tag">${t("result.tick")} ${String(totalTicks).padStart(3, "0")}</span>
          <span class="result-hud-sep">·</span>
          <span class="result-hud-tag">${t("result.grid")} ${gridSize}×${gridSize}</span>
          <span class="result-hud-sep">·</span>
          <span class="result-hud-chip result-hud-done">${t("result.live_logs")} · ON</span>
        </div>
      </header>

      <main class="screen-main result-main result-main-v3 tactical-no-scroll">
        <!-- 左栏 50%：hero(上) + 雷达/分析(中) + CTA(下) -->
        <div class="result-left">
          <!-- L1 结论：战果插画 + 大标题 + KDA 4 卡 -->
          <section class="result-hero-v3">
            <div class="hero-label">${t("result.mission_result")} · ${escapeHtml(opId)}</div>
            <div class="hero-word hero-word-${theme}">${heroWord}</div>
          </section>

          <!-- L1.5 KDA 4 卡细长条(替代 hero border-bottom) -->
          <div class="hero-kda-strip">${renderCompactMetrics(summary, totalTicks, durationClock, playerHp, enemyHp, shotsFired)}</div>

          <!-- L2 战斗分析区:三列(地图 - 雷达 - 战况分析/AAR) -->
          <section class="battle-analysis-zone">
            <div class="zone-head">
              <span class="zone-bar"></span>
              <span class="zone-title">01 / ${t("result.battle_analysis")}</span>
            </div>
            <div class="radar-analysis-row">
              <!-- 左:25×25 战术地图(终局画面,无底色半透明) -->
              <div class="battle-map-card">${renderBattleMap(summary)}</div>
              <!-- 中:6 维雷达(hover 显示维度说明) -->
              <div class="radar-zone">
                ${renderRadarSvg(tacticalDimensions, summary)}
              </div>
              <!-- 右:战况分析(AAR 5 点已移至右栏战术顾问顶部) -->
              <div class="analysis-stack">
                <div class="situation-zone">
                  <div class="sub-head">
                    <span class="sub-bar sub-bar-cyan"></span>
                    <span class="sub-title sub-title-cyan">${t("result.key_events")}</span>
                  </div>
                  <div class="event-list" id="result-event-list">
                    ${keyEvents.length > 0 ? keyEvents.map(renderEventRow).join("") : `<div class="result-empty-state">${t("result.key_events")}</div>`}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- L4 CTA(在左栏底部,中文按钮) -->
          <section class="cta-bar-v3">
            <button class="cta-btn cta-btn-primary" id="btn-rematch">${t("result.rematch")}</button>
            <button class="cta-btn cta-btn-secondary" id="btn-editor">${t("result.modify_code")}</button>
            ${v5NextLevel
              ? `<button class="cta-btn cta-btn-accent" id="btn-next-level" data-next="${escapeAttr(v5NextLevel)}">▶ 下一关</button>`
              : ""}
            <button class="cta-btn cta-btn-tertiary" id="btn-back-projects">${t("result.back_hub")}</button>
            ${v5PhaseUnlocked
              ? `<div class="v5-phase-unlock-toast">⬆ 阶段晋级 · 新关卡已解锁</div>`
              : ""}
          </section>

          <!-- L5 COMMIT HISTORY（P2-#3: Git commit 历史回看） -->
          <section class="commit-history-section" id="commit-history-section">
            <div class="zone-head">
              <span class="zone-bar"></span>
              <span class="zone-title">COMMIT HISTORY</span>
              <span class="zone-meta" id="commit-count">0 commits</span>
            </div>
            <div class="commit-list" id="commit-list">
              <div class="commit-empty">暂无提交记录</div>
            </div>
          </section>
        </div>

        <!-- 右栏 40%：战术复盘看板(地图 + 数据 + Chat) -->
        <section class="tactical-floating-panel">
          <!-- 4 角红 L 括号(8 段:每角 1 横 28px + 1 竖 28px) -->
          <i class="floating-corner floating-corner-tl"></i>
          <i class="floating-corner floating-corner-tr"></i>
          <i class="floating-corner floating-corner-bl"></i>
          <i class="floating-corner floating-corner-br"></i>
          <div class="floating-badge">▮ ${t("result.after_action")}</div>
          <div class="panel-content">
            <div class="zone-head">
              <span class="zone-bar"></span>
              <span class="zone-title">02 / ${t("result.tactical_review")}</span>
              <span class="zone-meta">${t("result.tactical_review")}</span>
            </div>

            <!-- 1. 战术顾问 Chat(KDA 4 卡已移到左栏 DEFEAT 下方) -->
            <div class="coach-zone" id="coach-zone">
              <!-- AAR 战术复盘 5 点(放在战术顾问对话最上面) -->
              <div class="aar-zone aar-zone-in-coach">
                <div class="sub-head">
                  <span class="sub-bar sub-bar-amber"></span>
                  <span class="sub-title sub-title-amber">AAR · ${t("result.tactical_review")}</span>
                </div>
                <div class="aar-list">
                  ${aarPoints.map((p) => renderAARRow(p)).join("")}
                </div>
              </div>
              <div class="sub-head">
                <span class="sub-bar sub-bar-green"></span>
                <span class="sub-title sub-title-green">${t("result.tactical_advisor")}</span>
                <span class="sub-meta" id="chat-rounds">0 / ${MAX_CHAT_ROUNDS} ${t("result.rounds")}</span>
              </div>
              <div class="chat-messages" id="chat-messages"></div>
              <div class="chat-input-row">
                <textarea class="ai-chat-input" id="ai-chat-input" rows="2" placeholder="${t("result.chat_placeholder")}" maxlength="200"></textarea>
                <button class="chat-send-btn" id="btn-ai-analyze" title="Send">▶</button>
              </div>
              <div class="ai-quick-prompt-row">
                ${getQuickChatPrompts().map((p) => `<button class="ai-quick-prompt" data-prompt="${escapeAttr(p)}">${escapeHtml(p)}</button>`).join("")}
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  `;

  // 路由事件
  root.querySelector("#btn-back-projects")?.addEventListener("click", (e) => {
    e.preventDefault();
    onNavigate(fromForge ? pathFor("forge") : pathFor("armory-hall"));
  });
  root.querySelector("#btn-editor")?.addEventListener("click", () => onNavigate(pathFor("editor", projectId)));
  root.querySelector("#btn-rematch")?.addEventListener("click", () => onNavigate(pathFor("battle", projectId)));
  // V5: 下一关 — 跳转到下一个关卡的编辑器
  root.querySelector("#btn-next-level")?.addEventListener("click", async (e) => {
    e.preventDefault();
    // 铸剑阁来源：回铸剑阁继续学习流
    if (fromForge) {
      onNavigate(pathFor("forge"));
      return;
    }
    const nextLevelId = (e.currentTarget as HTMLElement).dataset.next;
    if (!nextLevelId) return;
    const { createFromLevel } = await import("../storage/project-store");
    const levelStore = (await import("../storage/level-store")).createLevelStore();
    try {
      const level = await levelStore.load(nextLevelId);
      const proj = createFromLevel(nextLevelId, level.name, level.playerTemplate || "");
      onNavigate(pathFor("editor", proj.id));
    } catch {
      // fallback: 直接创建空 project
      const { DEFAULT_BOT_CODE } = await import("../storage/project-store");
      const proj = createFromLevel(nextLevelId, nextLevelId, DEFAULT_BOT_CODE);
      onNavigate(pathFor("editor", proj.id));
    }
  });

  // ===== P2-#3: COMMIT HISTORY 填充 =====
  const commitCountEl = root.querySelector<HTMLElement>("#commit-count");
  const commitListEl = root.querySelector<HTMLElement>("#commit-list");
  if (commitListEl) {
    const commits = listCommits(projectId);
    if (commitCountEl) commitCountEl.textContent = `${commits.length} commits`;
    if (commits.length === 0) {
      commitListEl.innerHTML = `<div class="commit-empty">暂无提交记录</div>`;
    } else {
      commitListEl.innerHTML = commits.slice(0, 20).map((c: Commit) => {
        const dt = new Date(c.timestamp);
        const timeStr = dt.toLocaleString();
        const preview = c.code.split("\n").slice(0, 3).join("\n") + (c.code.split("\n").length > 3 ? "\n..." : "");
        return `<div class="commit-item" data-hash="${c.hash}">
          <div class="commit-item-header">
            <span class="commit-hash">${c.hash.slice(0, 10)}</span>
            <span class="commit-message">${escapeHtml(c.message)}</span>
            <span class="commit-time">${timeStr}</span>
          </div>
          <pre class="commit-preview" hidden>${escapeHtml(preview)}</pre>
        </div>`;
      }).join("");
      // 点击展开/折叠代码预览
      commitListEl.querySelectorAll(".commit-item").forEach((item) => {
        const header = item.querySelector(".commit-item-header");
        const preview = item.querySelector(".commit-preview");
        if (header && preview) {
          header.addEventListener("click", () => {
            preview.toggleAttribute("hidden");
          });
        }
      });
    }
  }

  // ===== 战术顾问 Chat 状态机(多轮 + 流式 + 3 轮上限) =====
  type ChatState = {
    history: ChatMessage[];
    rounds: number;          // 已完成 user+coach 配对数
    streaming: boolean;
    abort: AbortController | null;
  };
  const initWeakest = [...tacticalDimensions].sort((a, b) => a.score - b.score)[0];
  const initStrongest = [...tacticalDimensions].sort((a, b) => b.score - a.score)[0];
  const chatState: ChatState = {
    history: [
      {
        id: "init-coach",
        role: "coach",
        // 单行简短 init,不强制换行,避免气泡过高挤压 chat-messages
        content: t("result.coach_init")
          .replace("{weakest}", initWeakest?.label ?? "?")
          .replace("{weakestScore}", String(initWeakest?.score ?? 0))
          .replace("{strongest}", initStrongest?.label ?? "?")
          .replace("{strongestScore}", String(initStrongest?.score ?? 0)),
      },
    ],
    rounds: 0,
    streaming: false,
    abort: null,
  };

  const chatMessagesEl = root.querySelector<HTMLElement>("#chat-messages");
  const chatInput = root.querySelector<HTMLTextAreaElement>("#ai-chat-input");
  const chatSendBtn = root.querySelector<HTMLButtonElement>("#btn-ai-analyze");
  const chatRoundsEl = root.querySelector<HTMLElement>("#chat-rounds");

  /** 渲染整条 chat history 到 #chat-messages */
  const renderChat = () => {
    if (!chatMessagesEl) return;
    chatMessagesEl.innerHTML = chatState.history.map(renderChatBubble).join("");
    // 滚动到底
    requestAnimationFrame(() => {
      chatMessagesEl.scrollTop = chatMessagesEl.scrollHeight;
    });
  };
  /** 更新轮次显示 + 输入可用性 */
  const updateRounds = () => {
    if (chatRoundsEl) chatRoundsEl.textContent = `${chatState.rounds} / ${MAX_CHAT_ROUNDS} ${t("result.rounds")}`;
    const reachedLimit = chatState.rounds >= MAX_CHAT_ROUNDS;
    const busy = chatState.streaming;
    if (chatInput) {
      chatInput.disabled = reachedLimit || busy;
      chatInput.placeholder = reachedLimit
        ? t("result.chat_limit_reached")
        : busy
          ? t("result.chat_analyzing")
          : t("result.chat_placeholder");
    }
    if (chatSendBtn) chatSendBtn.disabled = reachedLimit || busy;
  };

  /** 追加 user 消息并触发 AI 教练流式回复 */
  const sendUserMessage = async (text: string) => {
    const clean = text.trim();
    if (!clean) return;
    if (chatState.streaming) return;
    if (chatState.rounds >= MAX_CHAT_ROUNDS) return;

    // 1. user 消息入栈
    chatState.history.push({ id: `u-${Date.now()}`, role: "user", content: clean });
    renderChat();

    // 2. 临时 coach 占位(isStreaming:true)
    const coachId = `c-${Date.now()}`;
    chatState.history.push({ id: coachId, role: "coach", content: "", isStreaming: true });
    renderChat();
    updateRounds();

    // 3. 调 LLM 流式
    chatState.streaming = true;
    chatState.abort = new AbortController();
    updateRounds();

    try {
      const historyForLLM = chatState.history
        .filter((m) => m.id !== coachId && m.content)
        .map((m) => ({ role: m.role === "user" ? ("user" as const) : ("assistant" as const), content: m.content }));

      await generateBattleAnalysisStream({
        projectId,
        projectName: project.name,
        summary,
        tacticalDimensions: tacticalDimensions.map((d) => ({ label: d.label, score: d.score, verdict: d.verdict, evidence: d.evidence })),
        keyEvents: keyEvents.map((e) => ({ tick: e.tick, side: e.side, label: e.label, description: e.description })),
        logs: logs.map((l) => ({ tick: l.tick, tag: l.tag, team: l.team, message: l.message })),
        question: clean,
        history: historyForLLM,
        signal: chatState.abort.signal,
        onChunk: (_delta, accumulated) => {
          const msg = chatState.history.find((m) => m.id === coachId);
          if (msg) {
            msg.content = accumulated;
            // 流式时只更新最后一个 bubble 即可,避免全量重渲染抖动
            const bubble = chatMessagesEl?.querySelector<HTMLElement>(`[data-msg-id="${coachId}"] .chat-bubble-text`);
            if (bubble) {
              bubble.textContent = accumulated;
              if (chatMessagesEl) chatMessagesEl.scrollTop = chatMessagesEl.scrollHeight;
            }
          }
        },
      });
    } catch (e) {
      const msg = chatState.history.find((m) => m.id === coachId);
      if (msg) {
        msg.error = e instanceof Error ? e.message : String(e);
        msg.content = msg.content || t("result.coach_disconnected").replace("{error}", msg.error);
      }
    } finally {
      // 标记流式完成
      const msg = chatState.history.find((m) => m.id === coachId);
      if (msg) msg.isStreaming = false;
      chatState.streaming = false;
      chatState.abort = null;
      // 完成 1 轮(user + coach)
      chatState.rounds += 1;
      renderChat();
      updateRounds();
      if (chatInput && chatState.rounds < MAX_CHAT_ROUNDS) chatInput.focus();
    }
  };

  // 初始渲染 + 事件绑定
  renderChat();
  updateRounds();
  chatSendBtn?.addEventListener("click", () => {
    if (chatInput) {
      const text = chatInput.value;
      chatInput.value = "";
      void sendUserMessage(text);
    }
  });
  chatInput?.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      const text = chatInput.value;
      chatInput.value = "";
      void sendUserMessage(text);
    }
  });
  root.querySelectorAll<HTMLElement>(".ai-quick-prompt").forEach((button) => {
    button.addEventListener("click", () => {
      if (chatInput && !chatInput.disabled) {
        chatInput.value = button.dataset.prompt ?? "";
        chatInput.focus();
      }
    });
  });

  // Log 过滤（仅作用于 5 行精选）
  root.querySelectorAll<HTMLElement>("[data-log-filter]").forEach((button) => {
    button.addEventListener("click", () => {
      const filter = button.dataset.logFilter ?? "all";
      root.querySelectorAll<HTMLElement>("[data-log-filter]").forEach((item) => {
        item.classList.toggle("is-active", item === button);
      });
      root.querySelectorAll<HTMLElement>("#result-log-list .result-log-row").forEach((row) => {
        const rowFilter = row.dataset.filter ?? "all";
        row.style.display = filter === "all" || rowFilter === filter ? "" : "none";
      });
    });
  });

  // 调试用：暴露关键数据到 window（方便 console 验证）
  (window as unknown as { __resultDebug?: unknown }).__resultDebug = {
    theme,
    tacticalDimensions,
    keyEvents,
    logs,
    aarPoints,
    ticksPerSecond: ticksPerSecond.toFixed(2),
  };

  // ===== V16 新手引导：结果页渐进显隐 =====
  if (isOnboarding()) {
    initResultOnboarding(root, onNavigate);
  }

  // ===== V17 章一教学包装：首次通关庆祝 =====
  if (!isOnboarding()) {
    initFirstClearCelebration(root, projectId);
  }
}

/** V17: 首次通关庆祝 — 检测 sessionStorage flag 并显示庆祝 tip */
function initFirstClearCelebration(root: HTMLElement, projectId: string): void {
  try {
    const flagKey = `battlecode-first-clear-${projectId}`;
    const flag = sessionStorage.getItem(flagKey);
    if (flag !== "true") return;
    // 清除 flag（只庆祝一次）
    sessionStorage.removeItem(flagKey);

    // 延迟显示，等结果页渲染完成
    setTimeout(() => {
      const tip = document.createElement("div");
      tip.className = "onboarding-tip celebration-tip";
      tip.style.cssText = `
        position: fixed; bottom: 120px; left: 50%; transform: translateX(-50%);
        background: var(--amber-800); color: var(--white);
        padding: 8px 24px; font-family: monospace; font-size: 16px;
        border: 1px solid var(--amber-600); z-index: 100;
        animation: tipFadeIn 0.5s ease;
      `;
      tip.textContent = "◆ 首通！推演官已习得本章战术要义";
      root.appendChild(tip);

      // 3 秒后淡出
      setTimeout(() => {
        tip.style.transition = "opacity 0.8s";
        tip.style.opacity = "0";
        setTimeout(() => tip.remove(), 800);
      }, 3000);
    }, 800);
  } catch {
    // sessionStorage 不可用时静默跳过
  }
}

/* ============================================================
 * 数据函数（保留并扩展）
 * ============================================================ */

function buildTacticalDimensions(summary: BattleSummary): TacticalDimension[] {
  const deathPenalty = Math.min(45, summary.playerDeaths * 22 + Math.max(0, (summary.playerDeathTick ?? 0) > 0 ? 10 : 0));
  const survival = clamp(92 - deathPenalty + Math.max(0, Math.min(10, (summary.playerFinalHp ?? 0) / 10)), 10, 100);
  const pressure = clamp(45 + (summary.playerShotsFired ?? 0) * 2 + (summary.playerActions?.shoot ?? 0) * 4 - summary.playerDeaths * 8, 10, 100);
  const vision = clamp(38 + (summary.playerActions?.move ?? 0) * 4 + (summary.aiActions?.move ?? 0) * 1.5, 10, 100);
  const tempo = clamp(42 + summary.totalTicks / 8 + (summary.playerActions?.move ?? 0) * 2, 10, 100);
  const position = clamp(50 + (summary.playerFinalHp ?? 0) - (summary.enemyFinalHp ?? 0) * 0.25, 10, 100);
  const shooting = clamp(34 + (summary.playerActions?.hit ?? 0) * 10 + (summary.playerActions?.damage ?? 0) / 6, 10, 100);

  return [
    { key: "position", label: "位置控制", score: position, verdict: position >= 70 ? "站位有效" : position >= 45 ? "站位可用" : "站位松散", evidence: `基于终局 HP 与机动回合`, icon: "◎" },
    { key: "pressure", label: "进攻压制", score: pressure, verdict: pressure >= 70 ? "压制足够" : pressure >= 45 ? "压制一般" : "压制不足", evidence: `${summary.playerShotsFired ?? 0} 次射击, ${summary.playerActions?.damage ?? 0} 伤害`, icon: "▣" },
    { key: "vision", label: "视野/扫描", score: vision, verdict: vision >= 70 ? "侦察充分" : vision >= 45 ? "视野可用" : "侦察稀疏", evidence: `${summary.playerActions?.move ?? 0} 次位移`, icon: "◌" },
    { key: "shooting", label: "射击效率", score: shooting, verdict: shooting >= 70 ? "输出高效" : shooting >= 45 ? "命中正常" : "浪费较多", evidence: `${summary.playerActions?.hit ?? 0} 次命中`, icon: "✦" },
    { key: "survival", label: "生存能力", score: survival, verdict: survival >= 70 ? "站场稳定" : survival >= 45 ? "尚可支撑" : "容易被击穿", evidence: `${summary.playerDeaths} 次阵亡`, icon: "⟡" },
    { key: "tempo", label: "节奏掌控", score: tempo, verdict: tempo >= 70 ? "节奏主动" : tempo >= 45 ? "节奏平稳" : "节奏偏慢", evidence: `${summary.totalTicks} ticks`, icon: "▸" },
  ];
}

function normalizeKeyEvents(summary: BattleSummary): KeyEventView[] {
  const raw = summary.keyEvents ?? [];
  if (raw.length > 0) {
    return raw.slice(0, 3).map((event, index) => ({
      id: `k${index}`,
      tick: event.tickNum,
      side: event.actor === "team_a" ? "A" : "B",
      label: event.eventType.replace(/_/g, " ").toUpperCase(),
      description: event.description,
    }));
  }
  return [
    {
      id: "k0",
      tick: 0,
      side: "A",
      label: "OPENING",
      description: summary.reason || defaultReason(summary.winner === "team_a" ? "win" : summary.winner === "draw" ? "draw" : "lose"),
    },
  ];
}

function normalizeConsoleLogs(summary: BattleSummary): BattleLogView[] {
  const logs = summary.consoleLog ?? [];
  // v3 定稿：只取前 5 行（不再取 200）
  const views = logs.slice(0, 5).map((log) => ({
    tick: log.tick,
    tag: log.tag,
    team: log.team,
    message: log.message,
    fileLine: log.fileLine,
    filter: filterKey(log.tag, log.team),
  }));
  return views.length > 0 ? views : [{ tick: 0, tag: "INFO", team: "A", message: t("result.live_logs"), filter: "all" }];
}

function buildBattleAnalysis(summary: BattleSummary, tacticalDimensions: TacticalDimension[], keyEvents: KeyEventView[], logs: BattleLogView[]): BattleAnalysis {
  const overview = summary.reason || defaultReason(summary.winner === "team_a" ? "win" : summary.winner === "draw" ? "draw" : "lose");
  const strong = tacticalDimensions.reduce((best, item) => (item.score > best.score ? item : best), tacticalDimensions[0]);
  const weak = tacticalDimensions.reduce((worst, item) => (item.score < worst.score ? item : worst), tacticalDimensions[0]);
  const nextStep = `优先修复「${weak.label}」，把它从 ${weak.score} 提到 60 以上；同时保留你当前最强项「${strong.label}」的打法。`;
  const keyPointText = keyEvents[0]
    ? `关键转折出现在 T${keyEvents[0].tick}，${keyEvents[0].description}`
    : `日志里最显著的信号是 ${logs[0]?.message ?? "暂无可分析日志"}`;

  return { overview, keyPointText, nextStep, logsPreview: logs.slice(0, 5) };
}

/**
 * AAR 5 点规则推导（不调 LLM）
 * 01 最弱维度（红）：优先修复{label}（{score}）
 * 02 最强维度（绿）：保留{label}（{score}）优势
 * 03 关键事件（琥珀）：T{tick} {description}
 * 04 射击效率（按 shooting 维度 verdict 衍生）
 * 05 生存时长（按 survival 维度 + playerDeaths 衍生）
 */
function buildAARReviewList(
  dims: TacticalDimension[],
  events: KeyEventView[],
  _logs: BattleLogView[],
  summary: BattleSummary
): AARPoint[] {
  const sorted = [...dims].sort((a, b) => a.score - b.score);
  const weakest = sorted[0];
  const strongest = sorted[sorted.length - 1];
  const shooting = dims.find((d) => d.key === "shooting");
  const survival = dims.find((d) => d.key === "survival");
  const topEvent = events[0];

  const points: AARPoint[] = [];

  // 01 最弱维度（红）
  points.push({
    index: 1,
    tone: "lose",
    text: `优先修复「${weakest.label}」(${weakest.score}) — ${weakest.verdict}`,
  });

  // 02 关键事件（琥珀）
  if (topEvent) {
    points.push({
      index: 2,
      tone: "warn",
      text: `T${topEvent.tick} ${topEvent.description}`,
    });
  } else {
    points.push({
      index: 2,
      tone: "warn",
      text: "未捕获到显著关键事件，建议增加 scan 周期",
    });
  }

  // 03 生存时长（按 playerDeaths + survival verdict）
  const deaths = summary.playerDeaths ?? 0;
  if (deaths === 0) {
    points.push({ index: 3, tone: "good", text: `无阵亡，保持生存节奏` });
  } else if (deaths === 1) {
    points.push({ index: 3, tone: "warn", text: `1 次阵亡，${survival?.verdict ?? "生存一般"}` });
  } else {
    points.push({ index: 3, tone: "lose", text: `${deaths} 次阵亡，${survival?.verdict ?? "生存不稳"}` });
  }

  // 04 射击效率（按 shooting verdict）
  points.push({
    index: 4,
    tone: shooting && shooting.score >= 45 ? "good" : "lose",
    text: shooting ? `射击效率 ${shooting.score} · ${shooting.verdict}（${summary.playerActions?.hit ?? 0} 命中 / ${summary.playerShotsFired ?? 0} 次）` : "无射击数据",
  });

  // 05 最强维度（绿）
  points.push({
    index: 5,
    tone: "good",
    text: `保留「${strongest.label}」(${strongest.score}) 优势 · ${strongest.verdict}`,
  });

  return points;
}

/* ============================================================
 * 渲染函数
 * ============================================================ */

function renderEventRow(event: KeyEventView): string {
  const tone = event.side === "B" ? "enemy" : "team";
  const info = EVENT_TYPE_INFO[event.label.replace(/\s+/g, "_").toUpperCase()]
    ?? { name: event.label, meaning: "未识别事件类型", suggestion: "—" };
  const teamLabel = event.side === "A" ? "YOU" : "ENEMY";
  // 多行 tooltip:事件中文名+含义+建议+时间+队伍
  const tooltipText = `[${info.name}] ${event.label}\nMeaning: ${info.meaning}\nSuggestion: ${info.suggestion}\nTick: T${event.tick}  Team: ${teamLabel}`;
  return `
    <div class="event-row-v3 event-row-${tone}" data-tooltip="${escapeAttr(tooltipText)}">
      <span class="event-tick">T${String(event.tick).padStart(2, "0")}</span>
      <span class="event-tag">[${escapeHtml(event.label)}]</span>
      <span class="event-desc">${escapeHtml(event.description)}</span>
    </div>
  `;
}

/**
 * 关键事件类型说明 map(hover tooltip 用)
 * 覆盖 engine-scheduler.ts emit 的 9 种 key_event
 */
const EVENT_TYPE_INFO: Record<string, { name: string; meaning: string; suggestion: string }> = {
  FIRST_BLOOD:       { name: "First Blood", meaning: "First damage dealt to enemy this match", suggestion: "Dealing damage first gives tempo advantage" },
  COOLDOWN_TRIGGER:  { name: "Cooldown Exposed", meaning: "Opponent just fired and entered 30-tick cooldown", suggestion: "Cooldown window is a counter-attack opportunity, deal damage now" },
  DEATH:             { name: "Death", meaning: "Tank destroyed (HP reached 0)", suggestion: "Check HP management and positioning, avoid exposure in enemy range" },
  COLLISION:         { name: "Wall Collision", meaning: "Movement hit obstacle or boundary", suggestion: "Improve boundary checks and pathfinding, scan before moving" },
  COVER_USE:         { name: "Cover Used", meaning: "Protected behind cover, immune to damage", suggestion: "Maintain cover position, reduce exposure windows" },
  SCAN_FAILED:       { name: "Scan Failed", meaning: "Enemy beyond vision_range when scanning", suggestion: "Move closer before scanning, or use move to explore" },
  SCAN_DISCOVERY:    { name: "Scan Discovery", meaning: "Scan successfully locked enemy position (wrote to last_known_position)", suggestion: "Use last_known_position to calculate shoot path and range" },
  SHOOT_FAILED:      { name: "Shoot Failed", meaning: "Target not visible or out of range when shooting", suggestion: "Scan first to confirm is_visible, then enter range and shoot" },
  OPENING:           { name: "Opening", meaning: "Battle start (fallback when no key events)", suggestion: "Plan initial strategy, e.g. seize center or flank" },
};


/**
 * Chat 气泡渲染(头像 + 文本 + 流式光标)
 * - coach:左侧 CS-07 战术顾问头像 + 灰底气泡
 * - user:右侧 YOU 头像 + 青底气泡
 */
function renderChatBubble(msg: ChatMessage): string {
  const isCoach = msg.role === "coach";
  const avatarLabel = isCoach ? "CS" : "ME";
  const text = escapeHtml(msg.content || (msg.isStreaming ? "" : " "));
  const streamingClass = msg.isStreaming ? "chat-bubble-streaming" : "";
  // 注意:模板里 chat-bubble-text 前后不能有换行/空格
  // 因为 .chat-bubble white-space: pre-wrap 会把 HTML 缩进的换行当成空白行
  return `<div class="chat-message chat-message-${msg.role} ${streamingClass}" data-msg-id="${escapeAttr(msg.id)}"><div class="chat-avatar chat-avatar-${msg.role}">${avatarLabel}</div><div class="chat-bubble chat-bubble-${msg.role}"><div class="chat-bubble-text">${text}</div>${msg.isStreaming ? '<span class="chat-cursor">▊</span>' : ""}${msg.error ? `<div class="chat-bubble-error">${escapeHtml(msg.error)}</div>` : ""}</div></div>`;
}

function renderAARRow(p: AARPoint): string {
  return `
    <div class="aar-row aar-row-${p.tone}">
      <span class="aar-index">${String(p.index).padStart(2, "0")}</span>
      <span class="aar-text">${escapeHtml(p.text)}</span>
    </div>
  `;
}

/**
 * 战场地图快照（gridSize × gridSize 网格）
 * v34 升级：消费屏 4 battle_end 注入的真实数据（playerPos/enemyPos/coverBlocks/obstacleBlocks/spawnA/spawnB），
 * 缺失字段 fallback 占位（中心玩家 + 右上敌人 + 3 个假掩体，无障碍物/出生点）。
 * 与屏 4 战斗地图 100% 对应：同一 grid + 同一 token + 同一 terrain 数据。
 */
function renderBattleMap(summary: BattleSummary): string {
  const gridSize = summary.gridSize ?? 25;
  const playerPos = summary.playerPos ?? { x: Math.floor(gridSize / 2), y: Math.floor(gridSize / 2) };
  const enemyPos = summary.enemyPos ?? { x: Math.floor(gridSize * 0.7), y: Math.floor(gridSize * 0.3) };
  const coverBlocks = summary.coverBlocks ?? [
    { x: Math.floor(gridSize * 0.3), y: Math.floor(gridSize * 0.4) },
    { x: Math.floor(gridSize * 0.5), y: Math.floor(gridSize * 0.6) },
    { x: Math.floor(gridSize * 0.7), y: Math.floor(gridSize * 0.4) },
  ];
  // v34：真实地形数据（障碍物 + 出生点），屏 4 注入则用真实值，否则留空
  const obstacleBlocks = summary.obstacleBlocks ?? [];
  const spawnA = summary.spawnA;
  const spawnB = summary.spawnB;

  // 用 SVG 渲染（gridSize × gridSize，正方形 viewBox,content 满铺,无 margin）
  const cellSize = 20;          // 每格 20px
  const viewBoxSize = gridSize * cellSize;

  const lines: string[] = [];
  // 网格线
  for (let i = 0; i <= gridSize; i++) {
    const p = i * cellSize;
    lines.push(`<line x1="${p}" y1="0" x2="${p}" y2="${viewBoxSize}" class="map-grid-line" />`);
    lines.push(`<line x1="0" y1="${p}" x2="${viewBoxSize}" y2="${p}" class="map-grid-line" />`);
  }
  // v34：出生点（spawnA 青色 / spawnB 红色，半透明 2×2 格矩形 + 标签）
  if (spawnA) {
    const ax = spawnA.x * cellSize, ay = spawnA.y * cellSize;
    lines.push(`<rect x="${ax}" y="${ay}" width="${cellSize * 2}" height="${cellSize * 2}" class="map-token-spawn-a" />`);
    lines.push(`<text x="${ax + cellSize}" y="${ay + cellSize}" class="map-label-spawn" text-anchor="middle" dominant-baseline="central">A</text>`);
  }
  if (spawnB) {
    const bx = spawnB.x * cellSize, by = spawnB.y * cellSize;
    lines.push(`<rect x="${bx}" y="${by}" width="${cellSize * 2}" height="${cellSize * 2}" class="map-token-spawn-b" />`);
    lines.push(`<text x="${bx + cellSize}" y="${by + cellSize}" class="map-label-spawn" text-anchor="middle" dominant-baseline="central">B</text>`);
  }
  // v34：障碍物方块（深灰方形，不可穿越地形）
  obstacleBlocks.forEach((c) => {
    lines.push(`<rect x="${c.x * cellSize + 1}" y="${c.y * cellSize + 1}" width="${cellSize - 2}" height="${cellSize - 2}" class="map-token-obstacle" />`);
  });
  // 掩体方块（半透明青色方形，可穿越提供减伤）
  coverBlocks.forEach((c) => {
    lines.push(`<rect x="${c.x * cellSize + 2}" y="${c.y * cellSize + 2}" width="${cellSize - 4}" height="${cellSize - 4}" class="map-token-cover" />`);
  });
  // 玩家方块
  lines.push(`<rect x="${playerPos.x * cellSize - 6}" y="${playerPos.y * cellSize - 6}" width="12" height="12" class="map-token-player" />`);
  // 敌方方块
  lines.push(`<rect x="${enemyPos.x * cellSize - 6}" y="${enemyPos.y * cellSize - 6}" width="12" height="12" class="map-token-enemy" />`);

  // preserveAspectRatio="xMidYMid meet" 让 1:1 viewBox 完整显示(配合 row aspect-ratio 让 svg 元素 1:1)
  return `
    <svg class="map-svg" viewBox="0 0 ${viewBoxSize} ${viewBoxSize}" preserveAspectRatio="xMidYMid meet" aria-label="战场地图快照">
      <rect x="0" y="0" width="${viewBoxSize}" height="${viewBoxSize}" class="map-bg" />
      ${lines.join("")}
    </svg>
  `;
}

/**
 * 4 块紧凑指标卡（KDA / TICK / DURATION / HP）
 * A 字段固定 0（demo 1v1 无助攻）
 */
function renderCompactMetrics(
  _summary: BattleSummary,
  totalTicks: number,
  durationClock: string,
  playerHp: number,
  enemyHp: number,
  _shotsFired: number
): string {
  const playerKills = _summary.playerKills;
  const playerDeaths = _summary.playerDeaths;
  const kda = `${playerKills} / ${playerDeaths} / 0`;
  const tick = String(totalTicks).padStart(3, "0");
  const hp = `${playerHp} / ${enemyHp}`;
  const hpLow = playerHp === 0 || playerHp < enemyHp;

  return `
    <div class="metric-card">
      <div class="metric-label">${t("result.kda")}</div>
      <div class="metric-value">${escapeHtml(kda)}</div>
    </div>
    <div class="metric-card">
      <div class="metric-label">${t("result.total_ticks")}</div>
      <div class="metric-value">${escapeHtml(tick)}</div>
    </div>
    <div class="metric-card">
      <div class="metric-label">${t("result.duration")}</div>
      <div class="metric-value">${escapeHtml(durationClock)}</div>
    </div>
    <div class="metric-card ${hpLow ? "metric-card-warn" : ""}">
      <div class="metric-label">${t("result.final_hp")}</div>
      <div class="metric-value">${escapeHtml(hp)}</div>
    </div>
  `;
}

/**
 * v35 锐角战术雷达 v2 — 三个改造：
 * 1. 4 层同心圆 grid → 4 层正六边形 grid（6 维对应 6 角，对齐 Ardot 战术美学"锐角几何/无圆"）
 * 2. 去掉 linearGradient + feGaussianBlur 软渐变辉光 → 半透明实色 + 1.5px 实心描边（硬朗战术风）
 * 3. 加 "grow" 长出动效：
 *    - 整体 svg: scale(0.85) → scale(1) 0.6s ease-out
 *    - 数据多边形 fill: scale(0) → scale(1) 0.5s delay 0.2s
 *    - 数据多边形 stroke: stroke-dasharray 描线 0.9s delay 0.3s
 *    - 6 个数据点方块: 依次 scale(0) → scale(1)，每个 delay 0.5s + i*0.05s
 *    - 中心十字: pulse 1.6s 持续
 * 数据点 圆形 → 4×4 旋转 45° 菱形（更"瞄准镜"战术化），保留 <title> tooltip
 */
function renderRadarSvg(dims: TacticalDimension[], _summary: BattleSummary): string {
  const cx = 150;
  const cy = 150;
  const inner = 24;
  const outer = 100;
  const values = dims.map((d) => clamp(d.score, 8, 100));
  const angleStep = (Math.PI * 2) / values.length;
  const angleOffset = -Math.PI / 2;

  // 工具:给定半径生成六边形 6 顶点字符串(从 -PI/2 起,顺时针)
  const hexPoints = (radius: number): string => {
    const pts: string[] = [];
    for (let i = 0; i < values.length; i++) {
      const a = angleOffset + i * angleStep;
      pts.push(`${(cx + Math.cos(a) * radius).toFixed(1)},${(cy + Math.sin(a) * radius).toFixed(1)}`);
    }
    return pts.join(" ");
  };

  // 4 层六边形 grid(25% / 50% / 75% / 100% 缩放),半径从 inner 起算
  const rings = [0.25, 0.5, 0.75, 1].map((r) =>
    `<polygon points="${hexPoints(inner + (outer - inner) * r)}" class="radar-poly-grid" />`
  ).join("");

  // 6 条轴线(从中心放射到最外层六边形顶点)
  const axes = dims.map((_, i) => {
    const angle = angleOffset + i * angleStep;
    const x = cx + Math.cos(angle) * outer;
    const y = cy + Math.sin(angle) * outer;
    return `<line x1="${cx}" y1="${cy}" x2="${x.toFixed(1)}" y2="${y.toFixed(1)}" class="radar-axis" />`;
  }).join("");

  // 数据六边形顶点
  const dataPoints = values.map((v, i) => {
    const radius = inner + ((outer - inner) * v) / 100;
    const angle = angleOffset + i * angleStep;
    return {
      x: cx + Math.cos(angle) * radius,
      y: cy + Math.sin(angle) * radius,
    };
  });
  const dataPointsStr = dataPoints.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");

  // 6 个数据点(菱形方块) + 标签 + <title>
  // 菱形用 <rect> + transform rotate 45°,比 <circle> 更战术化
  const nodesAndLabels = dims.map((d, i) => {
    const value = values[i];
    const angle = angleOffset + i * angleStep;
    const radius = inner + ((outer - inner) * value) / 100;
    const px = cx + Math.cos(angle) * radius;
    const py = cy + Math.sin(angle) * radius;
    const tone = value < 40 ? "lose" : value < 70 ? "warn" : "good";
    const lblRadius = outer + 14;
    const lx = cx + Math.cos(angle) * lblRadius;
    const ly = cy + Math.sin(angle) * lblRadius;
    const anchor = Math.abs(Math.cos(angle)) < 0.2 ? "middle" : Math.cos(angle) > 0 ? "start" : "end";
    const tier = value >= 70 ? "GOOD" : value >= 40 ? "WARN" : "LOSE";
    const tooltipText = `${d.icon} ${d.label}（${d.key.toUpperCase()}）\n分数: ${value} / 100  [${tier}]\n评价: ${d.verdict}\n依据: ${d.evidence}`;
    return `
      <g class="radar-node-grp" style="--node-delay: ${(0.5 + i * 0.05).toFixed(2)}s">
        <rect x="${(px - 4).toFixed(1)}" y="${(py - 4).toFixed(1)}" width="8" height="8" class="radar-node-diamond radar-node-diamond-${tone}" transform="rotate(45 ${px.toFixed(1)} ${py.toFixed(1)})">
          <title>${escapeHtml(tooltipText)}</title>
        </rect>
      </g>
      <text x="${lx.toFixed(1)}" y="${ly.toFixed(1)}" text-anchor="${anchor}" class="radar-label" style="--label-delay: ${(0.7 + i * 0.05).toFixed(2)}s">
        <tspan class="radar-label-name">${RADAR_LABEL_SHORT[i]}</tspan>
        <tspan class="radar-label-val radar-label-val-${tone}" x="${lx.toFixed(1)}" dy="11">${d.score}</tspan>
      </text>
    `;
  }).join("");

  // 中心十字战术标记(2px × 8px 横竖,描边 style 配 --tactical-cyan)
  const centerMark = `
    <g class="radar-center-mark">
      <line x1="${cx - 6}" y1="${cy}" x2="${cx + 6}" y2="${cy}" class="radar-center-line" />
      <line x1="${cx}" y1="${cy - 6}" x2="${cx}" y2="${cy + 6}" class="radar-center-line" />
      <circle cx="${cx}" cy="${cy}" r="1.5" class="radar-center-dot" />
    </g>
  `;

  return `
    <div class="radar-area">
      <svg viewBox="0 0 300 300" class="radar-svg" aria-label="六维战术雷达">
        <g class="radar-grid-grp">${rings}</g>
        <g class="radar-axis-grp">${axes}</g>
        ${centerMark}
        <polygon points="${dataPointsStr}" class="radar-data-fill" />
        <polygon points="${dataPointsStr}" class="radar-data-stroke" />
        ${nodesAndLabels}
      </svg>
    </div>
  `;
}

/* ============================================================
 * 工具函数
 * ============================================================ */

function filterKey(tag: string, team: string): string {
  const normalizedTag = tag.toLowerCase();
  if (normalizedTag === "kill") return "kill";
  if (normalizedTag === "error") return "error";
  if (team.toUpperCase() === "A") return "team_a";
  if (team.toUpperCase() === "B") return "team_b";
  return "all";
}

function formatDuration(ms: number): string {
  const safe = Math.max(0, ms);
  const minutes = Math.floor(safe / 60000);
  const seconds = Math.floor((safe % 60000) / 1000);
  if (minutes > 0) {
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
  return `00:${String(seconds).padStart(2, "0")}`;
}

function defaultReason(theme: ResultTheme): string {
  if (theme === "win") return t("result.victory") + " · 你的 tank 击破敌方";
  if (theme === "draw") return t("result.draw") + " · 时限内未分胜负";
  return t("result.defeat") + " · 你的 tank 被击破";
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}

function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/`/g, "&#96;");
}

/** 3 个 quick prompt，每次渲染时取当前 locale 的翻译 */
function getQuickChatPrompts(): string[] {
  return [t("result.quick_prompt_lose"), t("result.quick_prompt_fix"), t("result.quick_prompt_aggressive")];
}


/** Chat 轮次上限(每轮 = 1 user + 1 coach) */
const MAX_CHAT_ROUNDS = 3;

/** Chat 消息结构 */
type ChatMessage = {
  id: string;
  role: "coach" | "user";
  content: string;
  isStreaming?: boolean;
  /** 流式错误(只用于 coach) */
  error?: string;
};

// ============================================================
// V3-005 Phase 3: 战术复盘（作战计划 vs };

// ===== V16 新手引导：结果页渐进显隐 =====

function initResultOnboarding(root: HTMLElement, onNavigate: (path: string) => void): void {
  setOnboardingStep("rs-4-1");

  // 隐藏除 Hero 以外的所有区域
  const sectionsToHide = [
    ".battle-analysis-zone",
    ".cta-bar-v3",
    ".commit-history-section",
    ".tactical-floating-panel",
  ];
  sectionsToHide.forEach((sel) => {
    const el = root.querySelector(sel) as HTMLElement | null;
    if (el) el.classList.add("onboarding-hidden");
  });

  // 4-2: 战斗分析（~2s 后显示）
  setTimeout(() => {
    setOnboardingStep("rs-4-2");
    const analysis = root.querySelector(".battle-analysis-zone") as HTMLElement | null;
    if (analysis) {
      analysis.classList.remove("onboarding-hidden");
      showResultTip(root, analysis, "终局快照 + 关键事件时间线。雷达图可 hover 查看 6 维战术评估", "bottom");
    }
  }, 2000);

  // 4-3: AAR + 顾问（~5s 后显示）
  setTimeout(() => {
    setOnboardingStep("rs-4-3");
    const panel = root.querySelector(".tactical-floating-panel") as HTMLElement | null;
    if (panel) {
      panel.classList.remove("onboarding-hidden");
      showResultTip(root, panel, "战术复盘：AAR 5 点分析 + AI 战术顾问。每场战斗后自动生成", "left");
    }
  }, 5000);

  // 4-4: CTA + Commit（~7s 后显示）
  setTimeout(() => {
    setOnboardingStep("rs-4-4");
    const cta = root.querySelector(".cta-bar-v3") as HTMLElement | null;
    const commit = root.querySelector(".commit-history-section") as HTMLElement | null;
    if (cta) {
      cta.classList.remove("onboarding-hidden");
      showResultTip(root, cta, "行动入口：REMATCH 再战 / MODIFY CODE 改代码 / BACK TO HUB 回铸剑阁", "top");
    }
    if (commit) commit.classList.remove("onboarding-hidden");

    // 4-5: 刃师痕迹 + 完成引导
    setTimeout(() => {
      setOnboardingStep("rs-4-5");
      showNarrativeBanner(root, onNavigate);
    }, 2500);
  }, 7000);

  // 修改「返回」按钮 → 完成引导
  const backBtn = root.querySelector("#btn-back-projects") as HTMLElement | null;
  if (backBtn) {
    backBtn.addEventListener("click", () => {
      completeOnboarding();
    }, { once: true });
  }
}

/** 在目标元素旁显示 tips（2 秒后消失） */
function showResultTip(
  root: HTMLElement,
  target: HTMLElement,
  text: string,
  position: "top" | "bottom" | "left" | "right"
): void {
  const tip = document.createElement("div");
  tip.className = `onboarding-tip onboarding-tip--${position} onboarding-tip--visible`;
  tip.textContent = text;
  root.appendChild(tip);

  // 定位
  const rect = target.getBoundingClientRect();
  const rootRect = root.getBoundingClientRect();
  switch (position) {
    case "top":
      tip.style.top = `${rect.top - rootRect.top - 50}px`;
      tip.style.left = `${rect.left - rootRect.left + rect.width / 2 - 140}px`;
      break;
    case "bottom":
      tip.style.top = `${rect.bottom - rootRect.top + 8}px`;
      tip.style.left = `${rect.left - rootRect.left + rect.width / 2 - 140}px`;
      break;
    case "left":
      tip.style.top = `${rect.top - rootRect.top + rect.height / 2 - 20}px`;
      tip.style.left = `${rect.left - rootRect.left - 300}px`;
      break;
  }

  setTimeout(() => {
    tip.classList.remove("onboarding-tip--visible");
    setTimeout(() => tip.remove(), 200);
  }, 2000);
}

/** 显示刃师痕迹叙事横幅 */
function showNarrativeBanner(root: HTMLElement, onNavigate: (path: string) => void): void {
  const banner = document.createElement("div");
  banner.className = "onboarding-result-narrative";
  banner.innerHTML = `
    <div class="onboarding-narrative-log">
      <div class="onboarding-narrative-line">&gt; 承刃链路稳定，神经延迟 &lt; 12ms</div>
      <div class="onboarding-narrative-line">&gt; 战术方案完成首次修正：添加开火指令</div>
      <div class="onboarding-narrative-line">&gt; 推演记录已归档：CB-0001</div>
      <div class="onboarding-narrative-line">&gt; 刃师已将此推演记录标记为「值得重看」</div>
      <div class="onboarding-narrative-quote">
        「承刃者，承的不是刀锋，<br>是判断的重量。」
        <span class="onboarding-narrative-attr">— 刃师，未标注日期</span>
      </div>
    </div>
    <button class="onboarding-narrative-btn pulse-highlight">继续 ▸ 进入铸剑阁</button>
  `;
  root.appendChild(banner);

  requestAnimationFrame(() => banner.classList.add("onboarding-narrative-visible"));

  const btn = banner.querySelector(".onboarding-narrative-btn") as HTMLButtonElement;
  btn.addEventListener("click", () => {
    completeOnboarding();
    banner.remove();
    onNavigate(pathFor("forge"));
  });
}

